import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { MSection, SectionHeader } from '@/components/marketing/section'

export const Route = createFileRoute('/_site/integrations/telegram')({
  component: TelegramIntegrationPage,
  head: () => ({
    meta: [
      { title: 'Telegram Monitoring Bot & Uptime Alerts — Uptrack' },
      { name: 'description', content: 'Get instant Telegram uptime alerts on your phone. Connect Uptrack to Telegram in under a minute. On every plan, free included.' },
      { property: 'og:title', content: 'Telegram Monitoring Bot & Uptime Alerts — Uptrack' },
      { property: 'og:description', content: 'Instant Telegram uptime alerts on your phone. Connect in under a minute. On every plan; the free plan has one chat channel.' },
      { property: 'og:image', content: 'https://uptrack.app/og/integrations-telegram.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/integrations/telegram' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://uptrack.app/integrations' },
            { '@type': 'ListItem', position: 3, name: 'Telegram', item: 'https://uptrack.app/integrations/telegram' },
          ],
        }),
      },
    ],
  }),
})

function TelegramIntegrationPage() {
  return (
    <>
      {/* Hero */}
      <section>
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="max-w-3xl">
            <h1 className="text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Telegram monitoring bot & uptime alerts.
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              Telegram delivers push notifications faster than email and works everywhere — desktop, phone, tablet. Get downtime alerts the instant they happen.
            </p>
            <p className="mt-3 text-sm text-muted-foreground">On every plan</p>
            <div className="mt-8">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
          </div>
        </div>
      </section>

      {/* Why */}
      <MSection tone="raised">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeader
              title="Why Telegram for monitoring alerts?"
              lead="Telegram push notifications are near-instant, work across every device, and don't get lost in inbox noise. For solo devs and small teams, Telegram is often the fastest way to know something is wrong."
            />
          </div>
          <div className="lg:col-span-7">
            <div className="rounded-lg border border-border bg-background p-6 text-sm">
              <p className="font-semibold text-status-down">Uptrack Alert</p>
              <p className="mt-2 text-muted-foreground">api.example.com is <span className="text-status-down font-medium">DOWN</span></p>
              <p className="text-muted-foreground">Status: 503 | Response: 12,340ms</p>
              <p className="text-muted-foreground">Failures: 3 | Started: 08:23 UTC</p>
            </div>
          </div>
        </div>
      </MSection>

      {/* How to set up */}
      <MSection>
        <SectionHeader title="How to set up Telegram alerts" />
        <ol className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
          {[
            { step: '1', title: 'Start the Uptrack bot', desc: 'Open Telegram and search for @UptrackBot. Send /start to get your unique chat ID.' },
                { step: '2', title: 'Add the channel in Uptrack', desc: 'Create a new alert channel, select Telegram, and paste your chat ID. Uptrack sends a test message to confirm.' },
                { step: '3', title: 'Assign monitors', desc: 'Attach the Telegram channel to your monitors. Set failure thresholds and enable recovery notifications.' },
          ].map((item) => (
            <li key={item.step} className="rounded-lg border border-border bg-card p-6">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background num text-sm font-semibold text-foreground">
                {item.step}
              </span>
              <h3 className="mt-4 text-[17px] font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-body">{item.desc}</p>
            </li>
          ))}
        </ol>
      </MSection>

      {/* What you get */}
      <MSection tone="raised">
        <SectionHeader title="What you get" />
        <ul className="mt-8 grid grid-cols-1 gap-x-10 gap-y-3 md:grid-cols-2">
          {[
            'Instant push notifications on downtime and recovery',
                  'Works on phone, tablet, and desktop',
                  'Status code, response time, and failure count in every alert',
                  'Group chat support for team channels',
                  'No bot hosting or infrastructure to manage',
                  'Per-monitor channel assignment',
                  'Available on every plan (one chat channel on the free plan)',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3 border-t border-border pt-3 text-[15px] text-body">
              <span aria-hidden="true" className="mt-0.5 font-mono text-status-up">✓</span>
              {item}
            </li>
          ))}
        </ul>
      </MSection>

      {/* CTA */}
      <section>
        <div className="container mx-auto px-4 py-12 md:py-[72px]">
          <div className="grid grid-cols-1 items-center gap-8 rounded-frame border border-border bg-card p-8 shadow-raised md:p-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Get Telegram alerts in under a minute</h2>
              <p className="mt-3 max-w-xl text-lead text-body">
                Every plan includes 30-second checks and alert confirmation.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Button size="lg" className="h-11 px-5 text-[15px]" asChild>
                <Link to="/pricing">
                  See plans
                  <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
              <p className="mt-3 text-sm text-muted-foreground">from $3/mo · 30-day money-back guarantee</p>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
